import React, { useCallback, useEffect, useRef } from 'react';
import './draggable-entity-card.css';
import {
	getCanvasEntityEntryCallerNodeDOMId,
	highlightElementIndefinetly,
	highlightElementSuccess,
	removeHighlightElementIndefinetly,
	removeHighlightElementSuccess,
} from '../../services/editor/ui';
import { Canvas, DraggableObject } from '../../lib/canvas';
import { ConnectionNodeType, EditorService } from '../../services/editor';
import {
	EntityWithLogicScopeState,
	CanvasEntityDisabledReason,
	checkIsMethod,
} from '@elyx-code/project-logic-tree';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';
import { mergeRefs } from '../../utils/react';

interface ILogicScopeCallerNodeProps {
	draggableObject: DraggableObject;
	entity: EntityWithLogicScopeState;
	project: EditorService;
	parentDisabledReasons: CanvasEntityDisabledReason[];
}

const LogicScopeCallerNode: React.FC<ILogicScopeCallerNodeProps> = (
	props: ILogicScopeCallerNodeProps
) => {
	const rootElementRef = useRef<HTMLButtonElement>(null);

	const entity: EntityWithLogicScopeState = props.entity;

	const isMethod = checkIsMethod(props.entity);

	// @ts-ignore
	const suggestion = entity.suggestion;

	const disabled =
		!!suggestion ||
		!!props.parentDisabledReasons.length ||
		(!!props.project.onGoingConnectionNodeType &&
			props.project.onGoingConnectionNodeType !== ConnectionNodeType.CalledBy);


	const resolveEntryTooltip = () => {
		return (
			<h3 style={{ marginTop: 0, marginBottom: 5 }}>
				Click and drag to connect to the execution of another node
			</h3>
		);
	};

	const memoizedCanvasEntryCallerTooltipBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				id={getCanvasEntityEntryCallerNodeDOMId(entity) + '--popup--body'}
				containerClasses={['tooltip', 'tooltip--large-font']}
			>
				{resolveEntryTooltip()}
			</CanvasPopupBaseComponent>
		),
		[],
	);

	const canvasEntryCallerTooltip = useCanvasPopup(
		{
			editor: props.project,
			id: getCanvasEntityEntryCallerNodeDOMId(entity) + '--popup',
			anchorQuerySelector: '#' + getCanvasEntityEntryCallerNodeDOMId(entity),
			placement: 'right',
			parentCanvasObject: props.draggableObject,
			arrowContainerSelector: '#' + getCanvasEntityEntryCallerNodeDOMId(entity) + '--popup--body',
		},
		memoizedCanvasEntryCallerTooltipBody,
		[],
	).setupHover({
		openDelay: 600,
		closeDelay: 400,
	});

	useEffect(() => {
		const handleConnectionStart = (payload: any) => {
			if (
				!!payload.compatible?.[entity.id] &&
				[ConnectionNodeType.CalledBy].includes(payload.nodeType)
			) {
				if (rootElementRef.current) {
					highlightElementIndefinetly(rootElementRef.current);
				}
			}
		};

		const handleConnectionEnd = () => {
			if (rootElementRef.current) {
				removeHighlightElementIndefinetly(rootElementRef.current);
				removeHighlightElementSuccess(rootElementRef.current);
			}
		};

		props.project.on('canvas-connection-start', handleConnectionStart);
		props.project.on('canvas-connection-end', handleConnectionEnd);

		return () => {
			props.project.off('canvas-connection-start', handleConnectionStart);
			props.project.off('canvas-connection-end', handleConnectionEnd);

			// Also clean up any lingering highlights just in case
			if (rootElementRef.current) {
				removeHighlightElementIndefinetly(rootElementRef.current);
				removeHighlightElementSuccess(rootElementRef.current);
			}
		};
	}, [
		props.project,
		entity.id,
		rootElementRef,
		highlightElementIndefinetly,
		removeHighlightElementIndefinetly,
		removeHighlightElementSuccess,
	]);

	const mergedRefs = !!canvasEntryCallerTooltip
		? mergeRefs(rootElementRef, canvasEntryCallerTooltip.anchorRef)
		: rootElementRef;

	return (
		<div
			className={[
				'canvas__node__vertical-center',
				'canvas__node__logic-scope-caller-node-stack',
				...(isMethod ? [] : ['canvas__node__float-right']),
				...(disabled ? ['disabled'] : []),
			].join(' ')}
			onClick={(e) => {
				e.stopPropagation();
			}}
			onMouseDown={(e) => {
				e.stopPropagation();
			}}
		>
			<span
				{...{
					[Canvas.OBJ_ATTR_NAME]:
						getCanvasEntityEntryCallerNodeDOMId(entity) + '--container',
				}}
				id={getCanvasEntityEntryCallerNodeDOMId(entity) + '--container'}
			>
				<button
					className='canvas__large-node canvas__large-node__caller'
					{...{
						[Canvas.OBJ_ATTR_NAME]: getCanvasEntityEntryCallerNodeDOMId(entity),
					}}
					disabled={disabled}
					id={getCanvasEntityEntryCallerNodeDOMId(entity)}
					onClick={(e) => {
						e.stopPropagation();
					}}
					ref={mergedRefs}
					onMouseOver={(e: any) => {
						if (disabled) {
							return;
						}

						if (
							!!props.project.canvas?.activeConnectionDrawing &&
							props.project.onGoingConnectionCompatibleEntities?.[entity.id] &&
							[ConnectionNodeType.CalledBy].includes(
								props.project.onGoingConnectionNodeType,
							)
						) {
							if (
								props.project.onGoingConnectionTarget &&
								[ConnectionNodeType.CalledBy].includes(
									props.project.onGoingConnectionNodeType,
								)
							) {
								props.project.onGoingConnectionSource = entity;
								props.project.onGoingConnectionSourceId =
									getCanvasEntityEntryCallerNodeDOMId(entity);
							}

							props.project.canvas?.activeConnectionDrawing.snap(
								getCanvasEntityEntryCallerNodeDOMId(entity),
							);

							removeHighlightElementIndefinetly(rootElementRef.current);
							highlightElementSuccess(rootElementRef.current);
						}
					}}
					onMouseLeave={(e: any) => {
						if (disabled) {
							return;
						}

						if (
							!!props.project.canvas?.activeConnectionDrawing &&
							props.project.onGoingConnectionCompatibleEntities?.[entity.id] &&
							[ConnectionNodeType.CalledBy].includes(
								props.project.onGoingConnectionNodeType,
							)
						) {
							removeHighlightElementIndefinetly(rootElementRef.current);
							highlightElementIndefinetly(rootElementRef.current);

							if (
								props.project.canvas?.activeConnectionDrawing?.snappedTo ===
								getCanvasEntityEntryCallerNodeDOMId(entity)
							) {
								props.project.canvas?.activeConnectionDrawing?.unSnap();
							}

							if (
								props.project.onGoingConnectionSource === entity &&
								props.project.onGoingConnectionSourceId ===
									getCanvasEntityEntryCallerNodeDOMId(entity) &&
								[ConnectionNodeType.CalledBy].includes(
									props.project.onGoingConnectionNodeType,
								)
							) {
								props.project.onGoingConnectionSource = null;
								props.project.onGoingConnectionSourceId = null;
							}
						}
					}}
					onMouseDown={(e) => {
						e.stopPropagation();

						if (disabled) {
							return;
						}

						props.project.startConnection({
							nodeType: ConnectionNodeType.EntryPointCaller,
							source: entity,
							sourceId: getCanvasEntityEntryCallerNodeDOMId(entity),
							draggableObject: props.draggableObject,
						});
					}}
					onMouseUp={(e) => {
						// Remove active class from connected/permanent node
					}}
				>
					<div
						{...{
							[Canvas.OBJ_ATTR_NAME]:
								getCanvasEntityEntryCallerNodeDOMId(entity) + '--center-ref',
						}}
						id={getCanvasEntityEntryCallerNodeDOMId(entity) + '--center-ref'}
						className='canvas__node__center-ref'
					/>
					<div>
						<i className='fa-solid fa-play' />
					</div>
				</button>
			</span>
		</div>
	);
};

export default LogicScopeCallerNode;
